// src/coreRepoJSONL.js
// Adaptador read-only para el CORE clínico con soporte de retroalimentación
import { SchemaValidator } from './SchemaValidator.js';
import { logger } from './Logger.js';

export function stableHash(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return "fnv1a32_" + h.toString(16).padStart(8, "0");
}

/**
 * Resuelve el "error típico" (la trampa didáctica) de un caso.
 *
 * La metadata guarda `error_tipico` como bandera boolean ("este caso tiene una
 * trampa señalable"), mientras que el TEXTO que se le muestra al alumno vive en
 * el archivo de decisiones. Como el merge es `{ ...decision, ...meta }`, el
 * boolean de la metadata pisaba el texto: la flashcard nunca mostraba la Trampa
 * y el Atlas llegaba a imprimir literalmente "Error Común: true".
 *
 * Devuelve el texto (string | undefined, nunca boolean) y una bandera separada.
 */
export function resolveErrorTipico(decision = {}, meta = {}) {
  const text = [decision.error_tipico, meta.error_tipico]
    .find(v => typeof v === 'string' && v.trim().length > 0);
  const flag = meta.error_tipico === true || decision.error_tipico === true || !!text;
  return { text: text || undefined, flag };
}

/**
 * Fusiona una decisión con su metadata en el nodo efectivo, normalizando
 * `error_tipico` a texto y exponiendo la bandera como `error_tipico_flag`.
 */
export function mergeNodeData(decision = {}, meta = {}) {
  const node = { ...decision, ...meta };
  const et = resolveErrorTipico(decision, meta);
  node.error_tipico = et.text;         // string | undefined (nunca boolean)
  node.error_tipico_flag = et.flag;    // boolean: ¿el caso tiene trampa señalada?
  return node;
}

async function fetchText(url) {
  try {
    // Habilitar caché nativo y de PWA para funcionamiento offline
    const res = await fetch(url, { headers: { 'Content-Type': 'application/json; charset=utf-8' } });
    if (!res.ok) {
        logger.error(`[FETCH] Fallo al cargar ${url}: ${res.status}`);
        return null;
    }
    return await res.text();
  } catch (err) {
    logger.error(`[FETCH] Error de conexión o CORS en ${url}:`, err);
    return null;
  }
}


function parseJSONL(text) {
  if (!text) return [];
  
  // Clean BOM and invisible chars
  let cleaned = text.replace(/^\uFEFF/, '');

  // Heuristic Repair: Arreglo de codificación para persistencia de tildes y eñes
  cleaned = cleaned
    .replace(/Ã­/g, 'í').replace(/Ã©/g, 'é').replace(/Ã¡/g, 'á')
    .replace(/Ã³/g, 'ó').replace(/Ãº/g, 'ú').replace(/Ã±/g, 'ñ')
    .replace(/Â¿/g, '¿').replace(/Â¡/g, '¡')
    .replace(/QuǸ/g, 'Qué').replace(/Cuǭl/g, 'Cuál')
    .replace(/segn/g, 'según').replace(/guas/g, 'guías')
    .replace(/antibitico/g, 'antibiótico').replace(/rgano/g, 'órgano')
    .replace(/perifǸrico/g, 'periférico').replace(/mǸdico/g, 'médico');

  // Normalize: ensure newlines between JSON objects if they are minified
  const normalized = cleaned.replace(/\}\s*\{/g, '}\n{');
  
  return normalized.split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map((line, index) => {
      try {
        return JSON.parse(line);
      } catch (e) {
        logger.error(`[parseJSONL] Line ${index + 1} parse error:`, e.message, line.substring(0, 50));
        return null;
      }
    })
    .filter(obj => obj !== null);
}

export const TAG_SYNONYMS = {
  'Cardiología': ['cardio', 'corazón', 'arritmia', 'infarto', 'hipertensión', 'taquicardia', 'bradicardia', 'stemi', 'acls'],
  'Ginecología/Obstetricia': ['gineco', 'embarazo', 'parto', 'eclampsia', 'ovario', 'uterino', 'obstetricia'],
  'Pediatría': ['pedia', 'neonato', 'lactante', 'niño', 'pediátrico', 'exantematicas'],
  'Infectología': ['infección', 'sepsis', 'fiebre', 'bacteriana', 'viral', 'vih', 'tuberculosis', 'meningitis'],
  'Gastroenterología': ['gastro', 'hígado', 'páncreas', 'intestino', 'digestivo', 'úlcera', 'abdomen_agudo'],
  'Urgencias/Trauma': ['trauma', 'politraumatismo', 'shock', 'hemorragia', 'intoxicación', 'atls', 'urgencias'],
  'Neurología': ['neuro', 'stroke', 'convulsión', 'coma', 'cefalea', 'ictus'],
  'Psiquiatría': ['psiqui', 'depresión', 'ansiedad', 'psicosis', 'esquizofrenia'],
  'Nefrología': ['riñón', 'renal', 'diálisis', 'electrolitos', 'nefro'],
  'Neumología': ['neumo', 'epoc', 'asma', 'embolia', 'pulmonar', 'neumonía', 'tep'],
  'Endocrinología': ['endocrino', 'diabetes', 'tiroides', 'metabólico', 'dka', 'insulina', 'hipoglucemia'],
  'Cirugía': ['cirugia', 'cirugía', 'apendicitis', 'colecistitis', 'quirúrgico'],
  'Medicina Interna': ['interna', 'general', 'medicina', 'metaclinico']
};

export function getBroadDomain(c) {
    const rawTags = (c.tags || []).map(t => t.toLowerCase());
    const rawTema = (c.tema || '').toLowerCase();
    
    for (const [domain, keywords] of Object.entries(TAG_SYNONYMS)) {
        if (keywords.some(kw => rawTags.includes(kw) || rawTema.includes(kw))) {
            return domain;
        }
    }
    
    if (rawTema.includes('cirugía')) return 'Cirugía';
    if (rawTema.includes('ginecología')) return 'Ginecología/Obstetricia';
    if (rawTema.includes('cardiología')) return 'Cardiología';
    if (rawTema.includes('urgencias')) return 'Urgencias/Trauma';
    
    return 'Medicina Interna';
}

export class CoreRepoJSONL {
  constructor() {
    this.byTag = new Map();
    this.nodeById = new Map();
    this.loadedGroups = new Set();
    this.manifest = null;
    this.indexBuilt = false;
    this.fetchedUrls = new Map();
  }

  async loadManifest(url = './docs/manifest_clinical.json') {
    const res = await fetch(url);
    this.manifest = await res.json();
    logger.debug(`[CoreRepo] Manifiesto cargado. Versión: ${this.manifest.version}`);
    
    // Auto-load General group as base
    await this.loadGroup('General');
    this.indexBuilt = true;
  }

  async loadGroup(groupName) {
    if (this.loadedGroups.has(groupName)) return;
    if (!this.manifest || !this.manifest.groups[groupName]) {
      logger.warn(`[CoreRepo] Grupo no encontrado en el manifiesto: ${groupName}`);
      return;
    }

    const group = this.manifest.groups[groupName];
    if (!group || !group.decisions) {
      logger.error(`[CoreRepo] Grupo ${groupName} malformado o sin decisiones.`);
      return;
    }

    logger.debug(`[CoreRepo] Cargando grupo: ${groupName}... Archivos:`, group.decisions);

    const [decisionsTexts, metadataTexts] = await Promise.all([
      Promise.all(group.decisions.map(async url => {
          if (!url) return '';
          if (this.fetchedUrls.has(url)) return this.fetchedUrls.get(url);
          const txt = await fetchText(url);
          // Temporary cache to avoid multi-fetch in same session
          this.fetchedUrls.set(url, txt);
          return txt;
      })),
      Promise.all((group.metadata || []).map(async url => {
          if (!url) return '';
          if (this.fetchedUrls.has(url)) return this.fetchedUrls.get(url);
          const txt = await fetchText(url);
          this.fetchedUrls.set(url, txt);
          return txt;
      }))
    ]);

    const decisions = decisionsTexts.flatMap(text => parseJSONL(text));
    const metadata = metadataTexts.flatMap(text => parseJSONL(text));

    // [GC OPTIMIZATION] Release large raw strings after parsing [cite: 3, 9]
    // Keeping these in fetchedUrls is redundant once they are in nodeById
    this.fetchedUrls.clear(); 

    const metaByNode = new Map();
    metadata.forEach(m => metaByNode.set(m.id || m.node_id, m));

    let nodesAdded = 0;
    for (const node of decisions) {
      if (!SchemaValidator.validateNode(node)) continue;
      const sanitized = SchemaValidator.sanitize(node);
      const id = sanitized.id;

      const meta = metaByNode.get(id) ?? {};
      const fullNode = mergeNodeData(sanitized, meta);

      if (this.nodeById.has(id)) {
        // SMART MERGE DUPLICATES: combine fields and options from both definitions
        const existing = this.nodeById.get(id);
        const merged = { ...existing, ...fullNode };

        if (Array.isArray(existing.opciones) && Array.isArray(fullNode.opciones)) {
          merged.opciones = fullNode.opciones.map(opt => {
            const extOpt = existing.opciones.find(o => o.id === opt.id);
            return extOpt ? { ...extOpt, ...opt } : opt;
          });
        }
        // El texto de la trampa gana sobre undefined; conservar la bandera.
        merged.error_tipico = fullNode.error_tipico ?? existing.error_tipico;
        merged.error_tipico_flag = fullNode.error_tipico_flag || existing.error_tipico_flag;
        this.nodeById.set(id, merged);
        continue;
      }

      this.nodeById.set(id, fullNode);

      const tags = meta.tags ?? sanitized.tags ?? [];
      tags.forEach(t => {
        if (!this.byTag.has(t)) this.byTag.set(t, new Set());
        this.byTag.get(t).add(id);
      });
      nodesAdded++;
    }

    this.loadedGroups.add(groupName);
    logger.debug(`[CoreRepo] Grupo ${groupName} cargado: ${nodesAdded} nodos nuevos. Total: ${this.nodeById.size}`);
  }

  /**
   * Returns standardized tags for a given specialty filter
   */
  _getNormalizedSearchTags(targetTags) {
    const expanded = new Set();
    targetTags.forEach(t => {
      expanded.add(t.toLowerCase());
      if (TAG_SYNONYMS[t]) {
        TAG_SYNONYMS[t].forEach(syn => expanded.add(syn.toLowerCase()));
      }
    });
    return Array.from(expanded);
  }

  async buildIndex() {
      // Legacy support or explicit full build if needed
      // If manifest is loaded, we usually don't need a full rebuild here.
      if (!this.manifest) await this.loadManifest();
  }

  async getNodeTags(node_id) {
    const node = this.nodeById.get(node_id);
    if (!node) throw new Error("ID no encontrado en el CORE: " + node_id);
    return (node.tags || []).slice();
  }

  getNode(node_id) {
    return this.nodeById.get(node_id);
  }

  async selectNodeIds(q) {
    if (!this.indexBuilt) throw new Error("Índice no construido.");

    if (q.ids && q.ids.length > 0) {
      return q.ids.filter(id => this.nodeById.has(id));
    }

    const tags = q.tags ?? [];
    let candidates = new Set(this.nodeById.keys());

    if (tags.length > 0) {
      candidates = new Set();
      const normalizedTags = this._getNormalizedSearchTags(tags);
      normalizedTags.forEach(t => {
        const matches = this.byTag.get(t) || new Set();
        matches.forEach(id => candidates.add(id));
      });
    }

    if (q.difficulty) {
      const filtered = new Set();
      for (const id of candidates) {
        const node = this.nodeById.get(id);
        if (node && (node.dificultad === q.difficulty || node.difficulty === q.difficulty)) filtered.add(id);
      }
      candidates = filtered;
    }

    if (Array.isArray(q.excludeIds) && q.excludeIds.length > 0) {
      q.excludeIds.forEach(id => candidates.delete(id));
    }

    return Array.from(candidates);
  }

  // --- SMART GENERATION ENGINE (The Director AI) ---

    async generateSessionDeck(userProfile, mode = 'RANKED', query = {}) {
    if (!this.indexBuilt) throw new Error("Índice no construido.");

    let allCases = Array.from(this.nodeById.values());
    
    // [VALIDACIÓN DE DATOS]: Filtrar casos huérfanos sin perla/justificacion o sin opciones
    allCases = allCases.filter(c => c.perla && c.justificacion && c.opciones && c.opciones.length > 0);

    const sessionDeck = [];
    const usedDomains = new Set();

    const getTopic = getBroadDomain; // Override old getTopic for internal FSRS uses
    const getDomain = getBroadDomain;

    // Apply Tags Filter if provided (e.g. for Specialty Guards)
    if (query.tags && query.tags.length > 0) {
      const targetTags = this._getNormalizedSearchTags(query.tags);
      allCases = allCases.filter(c => {
        const nodeTags = (c.tags || []).map(t => t.toLowerCase());
        const topic = (getTopic(c) || "").toLowerCase();
        const domain = (getDomain(c) || "").toLowerCase();
        return targetTags.some(tag => nodeTags.includes(tag) || topic.includes(tag) || domain.includes(tag));
      });
    }

    // Apply difficulty ceiling for library/study mode (query.difficulty provided)
    const diffCeiling = { 'baja': 1, 'media': 2, 'alta': 3 }[query.difficulty] ?? null;
    if (diffCeiling !== null) {
      allCases = allCases.filter(c => {
        const d = typeof c.dificultad === 'number' ? c.dificultad : { 'baja': 1, 'media': 2, 'alta': 3 }[c.dificultad] ?? 2;
        return d <= diffCeiling;
      });
    }

    // Hard Filters: exclude recently seen cases
    let candidatePool = allCases.filter(c => {
      const lastPlayed = userProfile.history?.[c.id] || 0;
      const hoursSince = (Date.now() - lastPlayed) / 3600000;
      if (hoursSince < 12) return false;
      if (mode === 'RANKED' && userProfile.playerLevel === 1 && (c.dificultad || 1) > 2) return false;
      return true;
    });

    if (candidatePool.length === 0) candidatePool = allCases; // Fallback if filtered too much

    // Scoring Function (FSRS-Lite + Diversity)
    const scoreCase = (c) => {
      let score = 10;

      const topic = getTopic(c);
      const domain = getDomain(c);

      // Intra-session diversity penalties (using Broad Domain now)
      if (usedDomains.has(domain)) score *= 0.1;

      // FSRS-Lite: Stability & Difficulty
      const history = userProfile.history || {};
      const topicStats = history[`__topic_${topic}`] || { stability: 0, difficulty: 5 };
      const retentionFactor = 1 / (1 + (topicStats.stability || 0));
      score *= retentionFactor;

      // Weak Tags (boosted by difficulty)
      if (c.tags) {
        const hasWeakTag = c.tags.some(tag => userProfile.weakTags?.includes(tag));
        if (hasWeakTag) score += 50 * (topicStats.difficulty / 5);
      }

      // Didactic Level Match
      if ((c.difficulty || 1) === userProfile.playerLevel) score += 20;

      // Spaced Repetition Bonus (> 7 days since last seen)
      const lastPlayed = userProfile.history?.[c.id] || 0;
      const daysSince = (Date.now() - lastPlayed) / 86400000;
      if (lastPlayed > 0 && daysSince > 7) score += 30;

      // Topic Cooling (seen in last 6 hours via another node)
      const topicCooling = Object.keys(history).some(id => {
        const hNode = this.nodeById.get(id);
        if (!hNode) return false;
        if (getTopic(hNode) === topic) {
          return ((Date.now() - history[id]) / 3600000) < 6;
        }
        return false;
      });
      if (topicCooling) score *= 0.5;

      score += Math.random() * 15; // Randomness
      return score;
    };

    // Weighted Random Selection
    const weightedRandomSelect = (pool) => {
      if (pool.length === 0) return null;
      let totalWeight = 0;
      const weighted = pool.map(item => {
        const weight = scoreCase(item);
        totalWeight += weight;
        return { item, weight };
      });

      let rng = Math.random() * totalWeight;
      for (const entry of weighted) {
        if (rng < entry.weight) return entry.item;
        rng -= entry.weight;
      }
      return pool[0];
    };

    // --- START ENARM SAGA 3-PHASE GENERATOR (BY BROAD DOMAIN) ---
    
    const uniqueDomains = Array.from(new Set(candidatePool.map(c => getBroadDomain(c))));

    // Select 1 Seed Domain based on weighted random (RESTRICTED to query.tags if provided)
    let seedDomain = null;
    let maxDomainScore = -1;
    uniqueDomains.forEach(domain => {
      // If filtering by tags, only consider domains within that query
      if (query.tags && query.tags.length > 0) {
          const targetTags = query.tags.map(t => t.toLowerCase());
          const domainNode = candidatePool.find(c => getBroadDomain(c) === domain);
          if (!domainNode || !domainNode.tags || !domainNode.tags.some(t => targetTags.includes(t.toLowerCase()))) return;
      }

      const repNode = candidatePool.find(c => getBroadDomain(c) === domain);
      if (repNode) {
        const score = scoreCase(repNode);
        if (score > maxDomainScore) {
          maxDomainScore = score;
          seedDomain = domain;
        }
      }
    });

    if (!seedDomain) {
      logger.warn("[CoreRepo] No se encontró un dominio semilla para los criterios dados. Usando Medicina Interna de fallback.");
      seedDomain = 'Medicina Interna';
    }

    const domainCases = candidatePool.filter(c => getBroadDomain(c) === seedDomain);

    // Helper to safely pop cases to avoid duplicates
    const availableDomainCases = [...domainCases];
    const extractCase = (filterFn) => {
        const idx = availableDomainCases.findIndex(filterFn);
        if (idx !== -1) {
            return availableDomainCases.splice(idx, 1)[0];
        }
        return null;
    };

    // FASE 1: Triage (Baja dificultad)
    let phase1 = extractCase(c => (c.dificultad || 1) <= 1);
    if (!phase1 && availableDomainCases.length > 0) phase1 = availableDomainCases.splice(0, 1)[0];
    if (phase1) sessionDeck.push(phase1.id);

    // FASE 2: Investigación / Diagnóstico (Media)
    let phase2 = extractCase(c => (c.dificultad || 1) === 2);
    if (!phase2 && availableDomainCases.length > 0) phase2 = availableDomainCases.splice(Math.floor(availableDomainCases.length / 2), 1)[0];
    if (phase2) sessionDeck.push(phase2.id);

    // FASE 3: Decisión Final / Letal (Difícil)
    let phase3 = extractCase(c => c.letal_si_falla || (c.dificultad || 1) >= 3);
    if (!phase3 && availableDomainCases.length > 0) phase3 = availableDomainCases.splice(availableDomainCases.length - 1, 1)[0];
    if (phase3) sessionDeck.push(phase3.id);

    // Rellenar con Especialidad Padre o Casos Aleatorios si el dominio no tenía 3
    while (sessionDeck.length < 3) {
      logger.warn(`[CoreRepo] Rellenando huecos para el dominio ${seedDomain}. Casos actuales: ${sessionDeck.length}`);
      // Tratar de rellenar de Medicina Interna (Cajón de Sastre) como fallback principal
      let fallbackPool = candidatePool.filter(c => getBroadDomain(c) === 'Medicina Interna' && !sessionDeck.includes(c.id));
      
      // Si Medicina Interna tampoco tiene, agarrar cualquiera que no esté repetido
      if (fallbackPool.length === 0) {
          fallbackPool = candidatePool.filter(c => !sessionDeck.includes(c.id));
      }

      if (fallbackPool.length > 0) {
          sessionDeck.push(fallbackPool[0].id);
      } else {
          // Absolute worst case: Reuse a case we already pushed (should almost never happen with 286 cases)
          sessionDeck.push(sessionDeck[0]); 
      }
    }

    return { deck: sessionDeck, specialty: seedDomain };
  }

  // --- END ENGINE ---

  async evaluate(node_id, decision_id) {
    if (!node_id) {
        logger.error("[CORE] Evaluación fallida: ID de nodo es indefinido.");
        return { result: 'error', reason: 'Missing node ID' };
    }

    const node = this.nodeById.get(node_id);
    if (!node) throw new Error(`Nodo no encontrado en el repositorio: ${node_id}`);

    // Support both schema styles:
    // Primary:     option.resultado === 'correcto'
    // Alternative: top-level respuesta_correcta field (set by SchemaValidator.sanitize)
    const correcta = node.opciones.find(o => o.id === node.respuesta_correcta || o.resultado === 'correcto');
    const seleccionada = node.opciones.find(o => o.id === decision_id);

    if (!correcta) throw new Error(`Node ${node_id}: no correct option found.`);
    if (!seleccionada) throw new Error(`Node ${node_id}: decision '${decision_id}' not found.`);

    const result = (decision_id === correcta.id) ? 'correct' : 'incorrect';

    // Lethal: explicit option flag OR node-level flag when answer is wrong
    const isLethal = seleccionada.resultado === 'error_letal' ||
      (node.letal_si_falla === true && result === 'incorrect');

    return {
      result,
      isLethal,
      rawOutcome: seleccionada.resultado,
      retro: node.justificacion ||
        seleccionada.retroalimentacion ||
        `Protocolo correcto: ${correcta.texto}.`,
      correct_option_text: correcta.texto,
      selected_option_text: seleccionada.texto || '',
      clinical_outcomes_hash: stableHash(`${node_id}|${decision_id}|${result}`),
      labs: node.labs || null,
      next_node_id: seleccionada.next_node_id || node.next_node_id || null
    };
  }
}
